import * as AbstractControls from '../fb/components/controls';
import { BuilderFields } from './BuilderFields';
import { BuilderHelper } from './BuilderHelper';
import { BuilderTask, BuilderControl, BuilderField } from '../fb/models/builder.model';

export interface ISorting extends Object {
  sortable?: boolean;
  asc?: boolean;
  active?: boolean;
  label?: string;
  key?: string;
}

export class FormBuilderHelper {

  constructor() {
    const lowerCaseObj = {};
    Object.keys(AbstractControls).map(key => (lowerCaseObj[key.toLowerCase()] = AbstractControls[key]));
    Object.keys(BuilderHelper.FieldsAssigns).map(key => {
      const target = BuilderHelper.FieldsAssigns[key].toLowerCase();
      key = key.toLowerCase();
      if (!lowerCaseObj[target] || lowerCaseObj[key]) {
        return;
      }
      lowerCaseObj[key] = lowerCaseObj[target];
    });
    this._abstractControls = lowerCaseObj;
  }

  get defaultSortings() {
    const sorting = (options: ISorting) => {
      return Object.assign({ sortable: false, active: false, asc: undefined }, options);
    };
    return {
      title: sorting({ sortable: true, label: 'Title' }),
      name: sorting({ label: 'Name' }),
      referenceName: sorting({ sortable: true, label: 'Reference Name' }),
      description: sorting({ label: 'Description' }),
      label: sorting({ label: 'Label' }),
      size: sorting({ label: 'Size' }),
    };
  }

  get basicFields() {
    return new BuilderFields().BasicFields;
  }
  private _abstractControls;

  static IsSection(control) {
    return control && control.hasOwnProperty('columns') && control.hasOwnProperty('rows');
  }

  sanitizeFields(fields: BuilderField[], filters, sortings: ISorting[]) {
    let final = fields;
    if (filters) {
      final = this.applyFilter(fields, filters);
    }
    return this.applySort(final, sortings);
  }

  applySort(arr: any[], sortings: ISorting[]) {
    const activeSortings = sortings.filter((sorting: ISorting) => sorting.active);
    let res = [];
    if (!activeSortings || !activeSortings.length) {
      return arr;
    }
    activeSortings.forEach((sorting: ISorting) => {
      res = arr.sort((a: BuilderField, b: BuilderField) => {
        if (a[sorting.key] === undefined || b[sorting.key] === undefined) {
          return true;
        }
        const first = sorting.asc ? a : b;
        const second = sorting.asc ? b : a;
        if (typeof a[sorting.key] === 'string') {
          return (first[sorting.key] || '').toLowerCase().localeCompare((second[sorting.key] || '').toLowerCase());
        }
        return first[sorting.key] - second[sorting.key];
      });
    });
    return res;
  }

  applyFilter(to: BuilderField[], _filter: string) {
    const filter = _filter.toLowerCase();
    const matchFilter = item => {
      if (!item || typeof item !== 'string') {
        return false;
      }
      return item.toLowerCase().indexOf(filter) !== -1;
    };
    return to.filter((item: BuilderField) => {
      return (
        matchFilter(item.name) ||
        matchFilter(item.dbTypeName) ||
        matchFilter(item.title) ||
        matchFilter(item.description) ||
        matchFilter(item.title) ||
        matchFilter(item.designCategory) ||
        matchFilter(BuilderHelper.ControlType(item.fieldType))
      );
    });
  }

  propertiesByControlType(control: BuilderField) {
    const AbstractControl = this.abstractControl(control);
    if (!AbstractControl) {
      return [];
    }
    return [AbstractControl.PROPERTIES()];
  }

  abstractControl(control, showUnknown: boolean = false) {
    let controlProperty;
    let abstractControl;

    if (FormBuilderHelper.IsSection(control)) {
      abstractControl = this._abstractControls['sectionlayout'];
    } else {
      controlProperty = `${BuilderHelper.ControlType(control.fieldType)}`.toLowerCase();
      abstractControl = this._abstractControls[controlProperty];
    }
    if (!abstractControl) {
      if (!showUnknown) {
        return;
      }
      return this._abstractControls['unknown'];
    }
    return abstractControl;
  }

  sanitizeProperties(properties: Object, form, control, model) {
    return Object.keys(properties).map((key, index) => {
      const result = ({ type, source }, value) => {
        setTimeout(() => {
          const control = form.controls[key];
          if (!control) {
            return;
          }
          control.setValue(value);
        }, 1);
        return { type, value, name: key, id: `${type}${index}`, source };
      };
      const property = properties[key];
      const { target, path } = this.parsePropertySource({ control, model }, property);
      return result(property, this.accessPath(target, path));
    });
  }

  move(array, from, to) {
    if (to === from) {
      return array;
    }

    const target = array[from];
    const increment = to < from ? -1 : 1;

    for (let k = from; k !== to; k += increment) {
      array[k] = array[k + increment];
    }
    array[to] = target;
    return array;
  }

  accessPath(obj, path, separator = '.') {
    const properties = Array.isArray(path) ? path : path.split(separator);
    return properties.reduce((prev, curr) => prev && prev[curr], obj);
  }

  parsePropertySource(targets, property) {
    const path = property.source.split('.');
    if (!path) {
      return;
    }
    const target = path.shift();
    return { target: targets[target], path: path.join('.') };
  }

  sanitizeTask(task: BuilderTask): any {
    const newTask = Object.assign(Object.create(task), task);
    const newModels = [];
    const allow = [...Object.keys(new BuilderControl()), 'sections'];
    const parse = (control, defaults = {}) => {
      if (control.cells) {
        control.cells.forEach(parse);
      }
      if (control.rows) {
        control.rows.forEach(parse);
      }
      if (control.sections) {
        control.sections.forEach(parse);
      }
      if (control.tabs) {
        control.sections.forEach(parse);
      }
      return sanitize(control, defaults);
    };
    const sanitize = (control, defaults = {}) => {
      // Runtime by FB
      if (control.hasOwnProperty('designCategory')) {
        control = this.deletePropertyNoRef(control, 'designCategory');
      }
      if (control.hasOwnProperty('iconClass')) {
        control = this.deletePropertyNoRef(control, 'iconClass');
      }
      if (control.fieldType && typeof control.fieldType === 'number') {
        control.fieldType = Object.keys(BuilderHelper.EFormElement).find(
          key => key === control.fieldType || BuilderHelper.EFormElement[key] === control.fieldType,
        );
      }
      const toMove: { [key: string]: BuilderField } = {};
      const newProps = Object.keys(control)
        .filter(key => {
          return allow.indexOf(key) === -1;
        })
        .map(key => {
          toMove[key] = control[key];
          control = this.deletePropertyNoRef(control, key);
          return key;
        });
      if (newProps.length) {
        newModels.push(Object.assign({}, { name: control.name, title: control.title }, toMove));
      }
      return Object.assign({}, defaults, control);
    };
    newTask.hiddenCells = task.hiddenCells.map(cell => {
      return parse(cell, { showHelp: false, colSpan: 0, rowSpan: 0, properties: {} });
    });
    newTask.tabs = task.tabs.map(cell => {
      return parse(cell, { colSpan: 0, rowSpan: 0 });
    });
    return { newTask, newModels };
  }

  /**
   * Deletes property from obj without hurting reference
   */
  deletePropertyNoRef(obj, property) {
    const result = {};
    if (!obj) {
      return;
    }
    Object.keys(obj).forEach(key => {
      if (key !== property) {
        result[key] = obj[key];
      }
    });
    return result;
  }
}
